import './horizontal-scroll-section.scss';
import Base from '../base/base';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

/**
 * Configuration options for HorizontalScrollSection
 */
interface HorizontalScrollSectionOptions {
  triggerSelector?: string;
  wrapperSelector?: string;
  contentSelector?: string;
  itemSelector?: string;
  scrub?: number;
  ease?: string;
  buffer?: number;
  bufferType?: 'fixed' | 'card-percentage';
  scrollCompression?: number;
  debug?: boolean;
  [key: string]: any;
}

/**
 * HorizontalScrollSection
 * 
 * Converts vertical scroll into horizontal translation for card-based layouts.
 * Uses GSAP ScrollTrigger to drive the animation based on scroll progress.
 * 
 * @requires gsap
 * @requires ScrollTrigger (GSAP plugin)
 */
export default class HorizontalScrollSection extends Base {
  private config: Required<Omit<HorizontalScrollSectionOptions, 'debug'>>;
  private trigger: Element | null;
  private wrapper: Element | null;
  private content: Element | null;
  private scrollTriggerInstance: ScrollTrigger | null = null;
  private resizeObserver: ResizeObserver | null = null;
  /**
   * @param options - Configuration options
   * @param options.triggerSelector - Selector for the scroll trigger element (tall container)
   * @param options.wrapperSelector - Selector for the sticky wrapper element
   * @param options.contentSelector - Selector for the scrollable content container
   * @param options.itemSelector - Selector for individual cards/items
   * @param options.scrub - ScrollTrigger scrub duration (smoothing/momentum)
   * @param options.ease - GSAP easing for scrub animation
   * @param options.buffer - Buffer space for last card (pixels or percentage based on bufferType)
   * @param options.bufferType - Type of buffer calculation ('fixed' for pixels or 'card-percentage')
   * @param options.scrollCompression - Percentage of section height to compress scroll range (0-100)
   * @param options.debug - Enable debug logging
   */
  constructor(options: HorizontalScrollSectionOptions = {}) {
    // Pass debug option to Base class
    super(options.debug || false);

    // Merge user config with defaults
    this.config = {
      triggerSelector: '.process-step-height',
      wrapperSelector: '.process-steps-wrapper',
      contentSelector: '.process-steps-content',
      itemSelector: '.process-step-item',
      scrub: 0.5,
      ease: 'power1.out',
      buffer: 80,
      bufferType: 'fixed',
      scrollCompression: 10,
      ...options
    };

    // Get DOM references
    this.trigger = document.querySelector(this.config.triggerSelector);
    this.wrapper = document.querySelector(this.config.wrapperSelector);
    this.content = document.querySelector(this.config.contentSelector);

    // Validate required elements exist
    if (!this.trigger || !this.wrapper || !this.content) {
      console.error('HorizontalScrollSection: Required DOM elements not found', {
        trigger: this.config.triggerSelector,
        wrapper: this.config.wrapperSelector,
        content: this.config.contentSelector
      });
      return;
    }

    // Initialize state
    this.scrollTriggerInstance = null;
    this.resizeObserver = null;

    // Bind methods to maintain context
    this.refresh = this.refresh.bind(this);

    // Start the show
    this.init();
  }

  /**
   * Initialize the horizontal scroll effect
   */
  init(): void {
    const dimensions = this.calculateDimensions();

    // Only create scroll animation if content is wider than viewport
    if (dimensions.shouldScroll) {
      this.createScrollAnimation(dimensions.scrollDistance);
    }

    // Watch for resize events
    this.setupResizeObserver();
  }

  /**
   * Calculate dimensions for horizontal scroll
   * Reads CSS values from DOM for card widths and spacing
   * 
   * @returns Object containing shouldScroll flag and scrollDistance
   */
  calculateDimensions(): { shouldScroll: boolean; scrollDistance: number } {
    // Early return if content is null
    if (!this.content) {
      return { shouldScroll: false, scrollDistance: 0 };
    }

    // Get all cards
    const cards = this.content.querySelectorAll(this.config.itemSelector);
    
    if (cards.length === 0) {
      return { shouldScroll: false, scrollDistance: 0 };
    }

    // Measure first card (assuming all cards are uniform)
    const firstCard = cards[0] as HTMLElement;
    const cardWidth = firstCard.offsetWidth; // Includes padding and border

    // Get gap from flex container
    const contentStyles = getComputedStyle(this.content);
    const gap = parseFloat(contentStyles.gap) || 0;

    // Calculate total content width
    const cardCount = cards.length;
    const totalCardWidth = cardWidth * cardCount;
    const totalGapWidth = gap * (cardCount - 1);
    const totalContentWidth = totalCardWidth + totalGapWidth;

    // Get visible viewport width (using wrapper as reference)
    const viewportWidth = (this.wrapper as HTMLElement)?.offsetWidth || 0;

    // Calculate buffer based on buffer type
    let buffer = 0;
    if (this.config.bufferType === 'card-percentage') {
      buffer = cardWidth * this.config.buffer;
    } else {
      buffer = this.config.buffer;
    }

    // Calculate how far we need to scroll (including buffer for last card breathing room)
    const scrollDistance = totalContentWidth - viewportWidth + buffer;

    return {
      shouldScroll: scrollDistance > 0,
      scrollDistance: Math.max(0, scrollDistance)
    };
  }

  /**
   * Create GSAP ScrollTrigger animation
   * Maps vertical scroll progress to horizontal translation
   * 
   * @param scrollDistance - Total horizontal distance to travel
   */
  createScrollAnimation(scrollDistance: number): void {
    // Kill existing instance if present
    if (this.scrollTriggerInstance) {
      this.scrollTriggerInstance.kill();
    }

    // Create horizontal translation animation
    const animation = gsap.to(this.content, {
      x: -scrollDistance,
      ease: 'none' // Linear progression; easing applied via scrub
    });

    // Create ScrollTrigger instance
    this.scrollTriggerInstance = ScrollTrigger.create({
      trigger: this.trigger,
      start: 'top top', // Start when trigger hits top of viewport
      end: `bottom-=${this.config.scrollCompression}% bottom`, // End with compression for reading time
      scrub: this.config.scrub, // Smooth catch-up effect
      animation: animation
    });
  }

  /**
   * Setup ResizeObserver to handle responsive recalculations
   * More efficient than window resize listeners
   */
  setupResizeObserver(): void {
    // Disconnect existing observer
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    // Create new observer
    this.resizeObserver = new ResizeObserver(() => {
      this.refresh();
    });

    // Watch content element for size changes
    if (this.content) {
      this.resizeObserver.observe(this.content);
    }
  }

  /**
   * Refresh the scroll animation
   * Called on resize or when dimensions change
   */
  refresh(): void {
    // Kill existing ScrollTrigger
    if (this.scrollTriggerInstance) {
      this.scrollTriggerInstance.kill();
      this.scrollTriggerInstance = null;
    }

    // Reset any inline transform styles
    gsap.set(this.content, { clearProps: 'x' });

    // Recalculate dimensions
    const dimensions = this.calculateDimensions();

    // Recreate animation if needed
    if (dimensions.shouldScroll) {
      this.createScrollAnimation(dimensions.scrollDistance);
    }
  }

  /**
   * Cleanup method
   * Destroys ScrollTrigger, disconnects observers, clears styles
   */
  destroy(): void {
    // Kill ScrollTrigger instance
    if (this.scrollTriggerInstance) {
      this.scrollTriggerInstance.kill();
      this.scrollTriggerInstance = null;
    }

    // Disconnect ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }

    // Clear inline styles
    gsap.set(this.content, { clearProps: 'x' });
  }
}